/**
 * useWorkspaceCompute — SSE-driven workspace compute lifecycle hook.
 *
 * Subscribes to the project SSE stream while `projectId` is non-null and
 * handles the two terminal states:
 *
 * - `COMPLETED`: invalidates the three workspace-scoped cache keys
 *   (locationMarkers, getOne, getAll), toasts that the location is ready
 *   (mirroring the History page's "Project created" toast, which the map
 *   flow otherwise lacks — see LOCATION_READY_MESSAGE), and, once the
 *   markers refetch settles, optionally auto-selects the single added marker.
 * - `FAILED`: rolls back the optimistic insert by removing `tempIds` from the
 *   `locationMarkers` cache immutably, then toasts the error message.
 *
 * Auto-select seam and id resolution
 * -----------------------------------
 * `MapSurface` owns the marker-selection state; there is no separate
 * selection context or hook reachable from here. Rather than coupling this hook
 * to MapSurface's internal `useState`, the hook accepts an optional
 * `onSelectMarker` callback in `ctx`. The caller (MapSurface) passes
 * its own selection setter. This is the minimal seam that keeps the hook
 * independently testable without reaching into the component tree.
 *
 * The optimistic `tempId` is a client-generated UUID never sent to (or
 * echoed by) the backend — the backend always assigns its own fresh id
 * (`POST …/locations` returns only a `Project`, no per-location id). So the
 * marker passed to `onSelectMarker` cannot be `tempId` itself: once the
 * markers refetch settles, this hook looks up the real marker by matching
 * `ctx.coordinates` against the refreshed cache (the same exact-coordinate
 * match `MapWorkspaceLayers` already uses for its coincident-pin warning)
 * and selects that id. If the just-awaited refetch still doesn't have it —
 * the backend can briefly lag between the SSE `COMPLETED` event and the row
 * becoming visible to a `GET` — `resolveRealMarkerIdWithRetry` re-invalidates
 * and looks again a few times before giving up and falling back to
 * `tempIds[0]`. This also means `onSettled` (which drops the run from
 * `activeComputeRuns`, and with it the "still computing" gate on the panel)
 * fires only once resolution is done, so the skeleton keeps showing through
 * the retries instead of the panel briefly selecting a real backend id that
 * doesn't exist yet.
 *
 * Auto-select is skipped if the user has since selected a different
 * location: `ctx.currentLocationIdRef` is read at the moment the run
 * completes (not captured at subscription time) so it reflects whatever the
 * user is looking at right now, not what was selected when the run launched.
 * The toast above still fires either way — the user gets told the location
 * is ready even when the panel doesn't jump to it.
 *
 * Concurrent runs
 * ---------------
 * Each call is an independent subscription keyed by `projectId`.
 * Mounting the hook twice with different `projectId` values opens two separate
 * SSE connections; they do not interfere.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { RefObject } from 'react';
import { toast } from 'sonner';
import { subscribeToProjectSSE } from '@/features/api/utils/subscribeToProjectSSE';
import {
  type ILocationMarkersCache,
  workspacesKeyFactory,
} from '@/features/workspaces/services/workspace.api';
import { toastBackendError } from '@/features/api/utils/toastBackendError';
import type { Coordinates } from '../context/ManualPinContext';

/** Toasted on COMPLETED, mirroring the History page's "Project created". */
const LOCATION_READY_MESSAGE = 'Location ready';

/**
 * Context bag passed to the hook by the caller.
 *
 * `workspaceId` — the workspace whose cache keys are invalidated / rolled back.
 * Must be the id captured at the time the compute run started, not a reactive
 * "active workspace" value, so workspace switches cannot corrupt the wrong cache.
 *
 * `tempIds` — the optimistic marker ids written during `onMutate` of
 * `useAddWorkspaceLocations`. Used to roll back the cache on FAILED, and as a
 * last-resort fallback if the real id can't be resolved on COMPLETED.
 *
 * `autoSelectSingle` — when `true` AND `tempIds.length === 1`, resolve and
 * select the real marker so the detail panel opens without an extra click.
 *
 * `coordinates` — the launch coordinates for the single-pin case, used to
 * resolve `tempIds[0]`'s real backend id once the markers refetch settles.
 * Required for `autoSelectSingle` to resolve correctly.
 *
 * `onSelectMarker` — optional callback invoked by the auto-select path. The
 * caller (MapSurface) binds its own marker-selection setter here.
 *
 * `existingMarkerIds` — ids of the workspace's markers that already existed
 * right before this run launched. Coordinates are not unique to one
 * location (recomputing at the same address reuses the shared `coordinates`
 * row but creates a new location id), so the coordinate match excludes these
 * to avoid resolving to a pre-existing marker instead of the one this run
 * just created.
 *
 * `currentLocationIdRef` — optional live pointer to whatever location id is
 * currently selected (e.g. `?location=<id>`). Checked at completion time to
 * skip auto-select if the user has since selected a different location — a
 * plain value would be stale by the time the (possibly long-running) SSE
 * stream settles, so this has to be a ref, read at call time, not captured
 * at subscription time like the rest of `ctx`.
 */
export interface WorkspaceComputeCtx {
  workspaceId: string;
  tempIds: string[];
  autoSelectSingle: boolean;
  coordinates?: Coordinates;
  onSelectMarker?: (markerId: string) => void;
  existingMarkerIds?: string[];
  currentLocationIdRef?: RefObject<string | null>;
  /**
   * Optional terminal callback invoked once on EITHER terminal (COMPLETED or
   * FAILED), after the cache invalidation / rollback has run. The caller
   * (MapSurface) uses it to drop the finished run from `activeComputeRuns` so
   * the workspace computing indicator clears on both outcomes,
   * regardless of whether the single-pin auto-select fired.
   */
  onSettled?: () => void;
  /** Optional progress callback, fired with the 0–100 percent on every SSE message. */
  onProgress?: (percent: number) => void;
}

/**
 * Subscribe to the project SSE stream and handle terminal outcomes for a
 * workspace compute run.
 *
 * @param projectId - The project id returned by the 202 `POST …/locations`.
 *   Passing `null` disables the subscription (no-op).
 * @param ctx - Workspace-scoped context. Captured at subscription time so
 *   workspace switches cannot corrupt a different workspace's cache.
 */
/**
 * Remove `tempIds` from the workspace's markers cache, immutably. Shared by
 * the SSE `FAILED` handler below and the explicit cancel flow
 * (`WorkspaceLocationPopup`'s Cancel button) — both need to undo the same
 * optimistic insert written by `useAddWorkspaceLocations`'s `onMutate`.
 * Silent no-op if the cache is absent.
 */
export function rollbackOptimisticMarkers(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  tempIds: string[],
): void {
  const markersKey = workspacesKeyFactory.locationMarkers(workspaceId).queryKey;
  const cached = queryClient.getQueryData<ILocationMarkersCache>(markersKey);
  if (cached === undefined) return;

  const tempIdSet = new Set(tempIds);
  queryClient.setQueryData<ILocationMarkersCache>(markersKey, {
    markers: cached.markers.filter((m) => !tempIdSet.has(m.id)),
  });
}

/**
 * Find the real marker matching the launch coordinates in the (freshly
 * refetched) markers cache, excluding `excludeIds` — markers that already
 * existed before this run launched. Coordinates alone are not a unique key
 * (recomputing at the same address shares the `coordinates` row with the
 * location it supersedes), so without the exclusion a coincident pre-existing
 * marker at the same coordinates would match instead of the new one. Falls
 * back to `fallbackId` (the tempId) if the cache is absent or holds no
 * eligible match yet.
 */
function resolveRealMarkerId(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  coordinates: Coordinates | undefined,
  excludeIds: ReadonlySet<string>,
  fallbackId: string,
): string {
  if (!coordinates) return fallbackId;
  const markersKey = workspacesKeyFactory.locationMarkers(workspaceId).queryKey;
  const cached = queryClient.getQueryData<ILocationMarkersCache>(markersKey);
  const match = cached?.markers.find(
    (m) =>
      m.latitude === coordinates.latitude &&
      m.longitude === coordinates.longitude &&
      !excludeIds.has(m.id),
  );
  return match?.id ?? fallbackId;
}

/** Extra lookups after the initial one, each preceded by a fresh refetch. */
const RESOLVE_RETRY_ATTEMPTS = 3;
const RESOLVE_RETRY_DELAY_MS = 300;

/**
 * Resolve the real marker id, retrying a few times if the id the just-awaited
 * refetch (`resolveRealMarkerId`) is still `fallbackId` — the backend can lag
 * briefly between the SSE `COMPLETED` event and the row becoming visible to a
 * `GET`, so one miss does not mean the marker will never appear. Each retry
 * re-invalidates the markers cache before looking again. Only after every
 * attempt misses does this give up and return `fallbackId`.
 *
 * Without coordinates, resolution can never succeed — skip retrying and
 * return `fallbackId` immediately.
 */
async function resolveRealMarkerIdWithRetry(
  queryClient: ReturnType<typeof useQueryClient>,
  workspaceId: string,
  coordinates: Coordinates | undefined,
  excludeIds: ReadonlySet<string>,
  fallbackId: string,
): Promise<string> {
  if (!coordinates) return fallbackId;

  for (let attempt = 0; attempt < RESOLVE_RETRY_ATTEMPTS; attempt++) {
    const resolved = resolveRealMarkerId(
      queryClient,
      workspaceId,
      coordinates,
      excludeIds,
      fallbackId,
    );
    if (resolved !== fallbackId) return resolved;
    if (attempt < RESOLVE_RETRY_ATTEMPTS - 1) {
      await new Promise((resolve) => setTimeout(resolve, RESOLVE_RETRY_DELAY_MS));
      await queryClient.invalidateQueries({
        queryKey: workspacesKeyFactory.locationMarkers(workspaceId).queryKey,
      });
    }
  }
  return fallbackId;
}

export function useWorkspaceCompute(projectId: string | null, ctx: WorkspaceComputeCtx): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (projectId === null) return;

    // Capture ctx values at subscription time so the closure stays stable and
    // workspace switches cannot bleed into a different workspace's cache.
    const {
      workspaceId,
      tempIds,
      autoSelectSingle,
      coordinates,
      onSelectMarker,
      existingMarkerIds,
      onSettled,
      onProgress,
      currentLocationIdRef,
    } = ctx;
    const excludeIds = new Set(existingMarkerIds ?? []);

    const controller = subscribeToProjectSSE(projectId, {
      // setIsLoading is required by the SSE helper interface; workspace compute
      // does not drive a loading spinner from this hook (the caller manages that
      // via the ManualPin computing state). No-op satisfies the contract.
      setIsLoading: () => undefined,

      setProgress: (_project, percent) => onProgress?.(percent),

      onSuccess: async () => {
        // 1. Await the markers refetch so auto-select can resolve the real id.
        await queryClient.invalidateQueries({
          queryKey: workspacesKeyFactory.locationMarkers(workspaceId).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: workspacesKeyFactory.getOne(workspaceId).queryKey,
        });
        void queryClient.invalidateQueries({
          queryKey: workspacesKeyFactory.getAll().queryKey,
        });

        // 2. Tell the user the location is ready, whether or not it auto-selects.
        toast.success(LOCATION_READY_MESSAGE);

        // 3. Auto-select the real marker, resolved by coordinates — but only if
        //    the user hasn't since selected a different location. Redirecting
        //    them away from whatever they're currently looking at would be an
        //    unwanted surprise, not a convenience.
        const userSelectedSomethingElse =
          currentLocationIdRef?.current != null && currentLocationIdRef.current !== tempIds[0];
        if (
          autoSelectSingle &&
          tempIds.length === 1 &&
          onSelectMarker !== undefined &&
          !userSelectedSomethingElse
        ) {
          onSelectMarker(
            await resolveRealMarkerIdWithRetry(
              queryClient,
              workspaceId,
              coordinates,
              excludeIds,
              tempIds[0],
            ),
          );
        }

        // 4. Notify the caller the run finished so it can drop it.
        onSettled?.();
      },

      onError: (errorMessage: string) => {
        // 1. Roll back the optimistic insert: remove tempIds from the markers
        //    cache immutably. If the cache is absent this is a silent no-op.
        rollbackOptimisticMarkers(queryClient, workspaceId, tempIds);

        // 2. Surface a sanitised error message to the user — routed through
        //    toastBackendError so backend-internal detail and the raw projectId
        //    are never shown verbatim (security: no info leakage).
        toastBackendError(errorMessage);

        // 3. Notify the caller the run finished so it can drop it.
        onSettled?.();
      },

      onInterrupted: () => {
        // The optimistic marker is dropped and the cache refetched: a location the backend did
        // finish without us shows up, one it never made does not. No toast: the usual cause is
        // a stale persisted run whose project is gone, and there is nothing to tell the user.
        rollbackOptimisticMarkers(queryClient, workspaceId, tempIds);
        void queryClient.invalidateQueries({
          queryKey: workspacesKeyFactory.locationMarkers(workspaceId).queryKey,
        });
        onSettled?.();
      },
    });

    return () => {
      controller.stop();
    };
    // ctx members are intentionally excluded from deps: they are captured
    // once at the moment projectId becomes non-null. Re-subscribing on every
    // ctx render would abort an in-flight SSE stream unnecessarily.
    // biome-ignore lint/correctness/useExhaustiveDependencies: ctx captured at subscription time by design
  }, [projectId, queryClient]);
}
